import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Heart, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

// Collage images of happy people
const COLLAGE_IMAGES = [
  { url: "https://images.unsplash.com/photo-1758613171720-74768b04e2a1?w=300&h=300&fit=crop", alt: "Friends taking selfie" },
  { url: "https://images.unsplash.com/photo-1763013259118-ffbc78b2bdb8?w=300&h=300&fit=crop", alt: "Mother and baby laughing" },
  { url: "https://images.unsplash.com/photo-1758526214005-acd3ec7560e2?w=300&h=300&fit=crop", alt: "Friends laughing together" },
  { url: "https://images.unsplash.com/photo-1585240976185-9a778493f176?w=300&h=300&fit=crop", alt: "Couple walking happy" },
  { url: "https://images.unsplash.com/photo-1769672773102-d0c9528b8e7a?w=300&h=300&fit=crop", alt: "Father and son" },
  { url: "https://images.unsplash.com/photo-1758272133831-510256416378?w=300&h=300&fit=crop", alt: "Group selfie rooftop" },
  { url: "https://images.unsplash.com/photo-1764751024389-857d08396423?w=300&h=300&fit=crop", alt: "Three women smiling" },
  { url: "https://images.unsplash.com/photo-1767082238371-d0407c323f52?w=300&h=300&fit=crop", alt: "Child on shoulders" },
  { url: "https://images.unsplash.com/photo-1758270705902-f50dde4add9f?w=300&h=300&fit=crop", alt: "Friends in classroom" },
  { url: "https://images.unsplash.com/photo-1619208110262-90c0438c174d?w=300&h=300&fit=crop", alt: "Couple kissing" },
  { url: "https://images.unsplash.com/photo-1758526214136-35cba385608b?w=300&h=300&fit=crop", alt: "Two women smiling" },
  { url: "https://images.unsplash.com/photo-1764816651418-df72a1471f9c?w=300&h=300&fit=crop", alt: "Happy family" },
  { url: "https://images.unsplash.com/photo-1758275557315-2685e63fa8d2?w=300&h=300&fit=crop", alt: "Friends in park" },
  { url: "https://images.unsplash.com/photo-1702456473497-2958d73adc80?w=300&h=300&fit=crop", alt: "Bride and groom" },
  { url: "https://images.unsplash.com/photo-1661173641095-4eb0a4b0911e?w=300&h=300&fit=crop", alt: "Man smiling" },
  { url: "https://images.unsplash.com/photo-1758526213942-662048bddf9f?w=300&h=300&fit=crop", alt: "Two friends laughing" },
  { url: "https://images.unsplash.com/photo-1571119702870-5fee1ed25dcf?w=300&h=300&fit=crop", alt: "Woman at celebration" },
  { url: "https://images.unsplash.com/photo-1653764658211-338864e378b7?w=300&h=300&fit=crop", alt: "Happy couple" },
  { url: "https://images.unsplash.com/photo-1505377059067-e285a7bac49b?w=300&h=300&fit=crop", alt: "Sisters laughing" },
  { url: "https://images.unsplash.com/photo-1706025000626-6ecadce42a82?w=300&h=300&fit=crop", alt: "Kids laughing" },
];

export default function Auth() {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    name: "",
    bio: "",
  });

  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate password confirmation for registration
    if (!isLogin && formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match!");
      return;
    }
    
    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success("Welcome back! 🎉");
      } else {
        await register(
          formData.email,
          formData.password,
          formData.name,
          formData.bio
        );
        toast.success("Account created! Welcome to HappyBook! 🌟");
      }
      navigate("/feed");
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left side - Image Collage */}
      <div className="hidden lg:block lg:w-1/2 relative overflow-hidden bg-stone-100">
        {/* Animated gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/30 via-transparent to-secondary/30 z-10"></div>
        
        {/* Image grid collage */}
        <div className="absolute inset-0 grid grid-cols-4 grid-rows-5 gap-1 p-1">
          {COLLAGE_IMAGES.map((img, index) => (
            <div
              key={index}
              className={`relative overflow-hidden ${
                // Make some images span 2 cells for variety
                index === 0 || index === 7 || index === 13
                  ? "col-span-2 row-span-2"
                  : ""
              }`}
              style={{
                animationDelay: `${index * 0.1}s`,
              }}
            >
              <img
                src={img.url}
                alt={img.alt}
                className="w-full h-full object-cover transition-transform duration-700 hover:scale-110"
                loading="lazy"
              />
            </div>
          ))}
        </div>

        {/* Glassmorphism quote overlay */}
        <div className="absolute bottom-8 left-8 right-8 z-20">
          <div className="glass rounded-3xl p-8">
            <h2 className="text-2xl font-bold text-foreground mb-3">
              "The best things in life are the people you love and the moments you share."
            </h2>
            <p className="text-muted-foreground font-handwriting text-xl">— HappyBook Community</p>
          </div>
        </div>

        {/* Floating stats */}
        <div className="absolute top-8 left-8 z-20">
          <div className="glass rounded-2xl px-6 py-4 flex items-center gap-3">
            <div className="flex -space-x-2">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-secondary border-2 border-white"
                />
              ))}
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">10,000+</p>
              <p className="text-xs text-muted-foreground">Happy members</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md space-y-8">
          {/* Back button */}
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </button>

          {/* Logo */}
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center">
              <Heart className="w-6 h-6 text-white" />
            </div>
            <span className="text-3xl font-bold text-foreground">HappyBook</span>
          </div>

          {/* Header */}
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              {isLogin ? "Welcome back!" : "Create your account"}
            </h1>
            <p className="text-muted-foreground mt-2">
              {isLogin
                ? "Sign in to continue spreading positivity"
                : "Join our community of happiness seekers"}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {!isLogin && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    name="name"
                    data-testid="auth-name-input"
                    placeholder="Enter your name"
                    value={formData.name}
                    onChange={handleChange}
                    required={!isLogin}
                    className="input-happy"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bio">Bio (optional)</Label>
                  <Textarea
                    id="bio"
                    name="bio"
                    data-testid="auth-bio-input"
                    placeholder="Tell us about yourself..."
                    value={formData.bio}
                    onChange={handleChange}
                    className="input-happy min-h-[80px] resize-none"
                  />
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                data-testid="auth-email-input"
                placeholder="you@example.com"
                value={formData.email}
                onChange={handleChange}
                required
                className="input-happy"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  data-testid="auth-password-input"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  minLength={6}
                  className="input-happy pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Confirm Password (only for registration) */}
            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    data-testid="auth-confirm-password-input"
                    placeholder="••••••••"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    minLength={6}
                    className="input-happy pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    data-testid="toggle-confirm-password-visibility"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>
            )}

            <Button
              type="submit"
              data-testid="auth-submit-btn"
              disabled={loading}
              className="w-full btn-primary py-6"
            >
              {loading
                ? "Please wait..."
                : isLogin
                ? "Sign In"
                : "Create Account"}
            </Button>
          </form>

          {/* Toggle */}
          <div className="text-center">
            <p className="text-muted-foreground">
              {isLogin ? "Don't have an account?" : "Already have an account?"}{" "}
              <button
                type="button"
                data-testid="auth-toggle-btn"
                onClick={() => setIsLogin(!isLogin)}
                className="text-primary font-semibold hover:underline"
              >
                {isLogin ? "Sign up" : "Sign in"}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
